import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Card, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useOrderWizardContext } from '../OrderWizardProvider';

const FinalSummaryStep = ({ availableAccounts }) => {
  const { orderData } = useOrderWizardContext();



  // Función para calcular total de fees si no está presente
  const calculateTotalFees = () => {
    if (orderData.totalFeesAmount) {
      return parseFloat(orderData.totalFeesAmount);
    }
    
    // Calcular basado en los fees individuales
    const gross = parseFloat(orderData.inversionBruta) || 0;
    let totalFees = 0;
    
    // Broker Fee
    if (orderData.brokerFee) {
      const brokerFeeValue = parseFloat(orderData.brokerFee) || 0;
      if (orderData.brokerFeeType === 'percentage') {
        totalFees += (gross * brokerFeeValue / 100);
      } else {
        totalFees += brokerFeeValue;
      }
    }
    
    // Media Fee
    if (orderData.mediaFee) {
      const mediaFeeValue = parseFloat(orderData.mediaFee) || 0;
      if (orderData.mediaFeeType === 'percentage') {
        totalFees += (gross * mediaFeeValue / 100);
      } else {
        totalFees += mediaFeeValue;
      }
    }
    
    // Admin Fee
    if (orderData.feeAdmin) {
      const adminFeeValue = parseFloat(orderData.feeAdmin) || 0;
      if (orderData.feeAdminType === 'percentage') {
        totalFees += (gross * adminFeeValue / 100);
      } else {
        totalFees += adminFeeValue;
      }
    }
    
    return totalFees;
  };



  // Función para obtener el color del impacto de fees
  const getFeeImpactColor = () => {
    const bruto = parseFloat(orderData.inversionBruta) || 0;
    const totalFeesAmount = calculateTotalFees();
    
    if (bruto === 0) return 'text-muted';
    
    const diff = (totalFeesAmount / bruto * 100);
    return diff > 15 ? 'text-danger' : diff > 8 ? 'text-warning' : 'text-success';
  };

  // Validaciones finales
  const getValidationStatus = () => {
    const issues = [];
    
    if (!orderData.clienteId) issues.push('Cliente no seleccionado');
    if (!orderData.businessUnit) issues.push('Business Unit no seleccionado');
    if (!orderData.descripcion) issues.push('Descripción faltante');
    if (!orderData.fechaDesde) issues.push('Fecha de inicio no configurada');
    if (!orderData.fechaHasta) issues.push('Fecha de fin no configurada');
    if (!orderData.inversionBruta || parseFloat(orderData.inversionBruta) <= 0) issues.push('Inversión bruta inválida');
    if (!orderData.nivelAsignacion) issues.push('Nivel de asignación no seleccionado');
    
    // *** VALIDACIÓN ACTUALIZADA PARA IDs REALES ***
    if (orderData.nivelAsignacion === 'Campañas') {
      if (!orderData.campaignId) issues.push('Campaña no seleccionada');
    } else if (orderData.nivelAsignacion === 'Cuenta') {
      if (!orderData.accountId) issues.push('Cuenta no seleccionada');
    }
    
    return issues;
  };

  const validationIssues = getValidationStatus();
  const isReadyToSubmit = validationIssues.length === 0;

  return (
    <div>
      <div className="mb-4">
        <h5 className="text-primary mb-2">
          <FontAwesomeIcon icon="check-circle" className="me-2" />
          Resumen Final y Validación
        </h5>
        <p className="text-muted mb-0">
          Revisa todos los detalles antes de crear la orden de inserción
        </p>
      </div>

      <Row className="g-4">
        {/* Estado de Validación */}
        <Col xs={12}>
          {isReadyToSubmit ? (
            <Alert variant="success" className="d-flex align-items-center">
              <FontAwesomeIcon icon="check-circle" className="me-2 fs-4" />
              <div>
                <div className="fw-bold">¡Orden lista para crear!</div>
                <div>Todos los campos requeridos están completados correctamente.</div>
              </div>
            </Alert>
          ) : (
            <Alert variant="warning" className="d-flex align-items-start">
              <FontAwesomeIcon icon="exclamation-triangle" className="me-2 fs-4 mt-1" />
              <div>
                <div className="fw-bold">Faltan algunos campos requeridos:</div>
                <ul className="mb-0 mt-2">
                  {validationIssues.map((issue, index) => (
                    <li key={index}>{issue}</li>
                  ))}
                </ul>
              </div>
            </Alert>
          )}
        </Col>

        {/* Resumen Completo */}
        <Col xs={12}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom">
              <h6 className="text-warning mb-0">
                <FontAwesomeIcon icon="file-alt" className="me-2" />
                Resumen Completo de la Orden
              </h6>
            </Card.Header>
            <Card.Body className="p-4">
              {/* Información Principal - Rediseñada */}
              <div className="mb-4">
                <Row className="g-3">
                  {/* Cliente */}
                  <Col md={6}>
                    <div className="d-flex align-items-center p-3 bg-light rounded">
                      <div className="me-3">
                        <div className="avatar-icon bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{width: '40px', height: '40px'}}>
                          <FontAwesomeIcon icon="user" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <small className="text-muted fw-semibold d-block text-uppercase">Cliente</small>
                        <div className="fw-bold text-dark h6 mb-0">{orderData.clienteName || 'No seleccionado'}</div>
                        <small className="text-muted">ID: {orderData.clienteId || 'N/A'}</small>
                      </div>
                    </div>
                  </Col>

                  {/* Business Unit */}
                  <Col md={6}>
                    <div className="d-flex align-items-center p-3 bg-light rounded">
                      <div className="me-3">
                                                 <div className="avatar-icon bg-info text-white rounded-circle d-flex align-items-center justify-content-center" style={{width: '40px', height: '40px'}}>
                           <FontAwesomeIcon icon="cogs" />
                         </div>
                      </div>
                      <div className="flex-1">
                        <small className="text-muted fw-semibold d-block text-uppercase">Business Unit</small>
                        <div className="fw-bold text-dark h6 mb-0">{orderData.businessUnit || 'No especificado'}</div>
                      </div>
                    </div>
                  </Col>

                  {/* Cuenta/Campaña Seleccionada - ACTUALIZADO */}
                  <Col md={6}>
                    <div className="d-flex align-items-center p-3 bg-light rounded">
                      <div className="me-3">
                        <div className="avatar-icon bg-success text-white rounded-circle d-flex align-items-center justify-content-center" style={{width: '40px', height: '40px'}}>
                          <FontAwesomeIcon icon={orderData.nivelAsignacion === 'Campañas' ? 'bullseye' : 'user-circle'} />
                        </div>
                      </div>
                      <div className="flex-1">
                        <small className="text-muted fw-semibold d-block text-uppercase">
                          {orderData.nivelAsignacion === 'Campañas' ? 'Campaña' : 'Cuenta'}
                        </small>
                        <div className="fw-bold text-dark h6 mb-0">
                          {orderData.nivelAsignacion === 'Campañas' ? (
                            orderData.selectedCampaigns?.length > 0
                              ? `${orderData.selectedCampaigns.length} campaña(s)`
                              : orderData.campaignId ? `Campaña ID: ${orderData.campaignId}` : 'No seleccionada'
                          ) : (
                            orderData.accountId ? `Cuenta ID: ${orderData.accountId}` : 'No seleccionada'
                          )}
                        </div>
                        {orderData.nivelAsignacion === 'Campañas' && orderData.selectedCampaigns?.length > 0 && (
                          <div className="mt-1" style={{ maxHeight: '100px', overflowY: 'auto', fontSize: '0.75rem' }}>
                            {orderData.selectedCampaigns.map((c, i) => (
                              <div key={i} className="text-muted text-truncate">{c.campaignName}</div>
                            ))}
                          </div>
                        )}
                        <small className="text-muted">
                          Nivel: {orderData.nivelAsignacion || 'No especificado'}
                          {orderData.nivelAsignacion === 'Campañas' && orderData.selectedAccountForCampaigns && (
                            <div>Cuenta: {orderData.selectedAccountForCampaigns}</div>
                          )}
                        </small>
                      </div>
                    </div>
                  </Col>

                  {/* Nivel de Asignación */}
                  <Col md={6}>
                    <div className="d-flex align-items-center p-3 bg-light rounded">
                      <div className="me-3">
                        <div className="avatar-icon bg-warning text-white rounded-circle d-flex align-items-center justify-content-center" style={{width: '40px', height: '40px'}}>
                          <FontAwesomeIcon icon="layer-group" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <small className="text-muted fw-semibold d-block text-uppercase">Nivel de Control</small>
                        <div className="fw-bold text-dark h6 mb-0">
                          {orderData.nivelAsignacion || 'No definido'}
                        </div>
                        <small className="text-muted">
                          {orderData.nivelAsignacion === 'Campañas' ? 
                            'Control granular por campaña' : 
                            'Control agregado por cuenta'
                          }
                        </small>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Información Financiera */}
              <Row className="mb-4">
                <Col lg={4} className="mb-3">
                  <div className="bg-light p-3 rounded text-center">
                    <small className="text-muted fw-semibold d-block">Inversión Bruta</small>
                    <div className="h4 fw-bold text-success mb-0">
                      ${parseFloat(orderData.inversionBruta || 0).toLocaleString('es-ES', { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                </Col>
                <Col lg={4} className="mb-3">
                  <div className="bg-light p-3 rounded text-center">
                    <small className="text-muted fw-semibold d-block">Total Fees</small>
                    <div className="h4 fw-bold text-danger mb-0">
                      ${calculateTotalFees().toLocaleString('es-ES', { minimumFractionDigits: 2 })}
                    </div>
                    <div className="small text-muted mt-1">
                      {orderData.brokerFee && parseFloat(orderData.brokerFee) > 0 && (
                        <div>Broker: {orderData.brokerFee}%</div>
                      )}
                      {orderData.mediaFee && parseFloat(orderData.mediaFee) > 0 && (
                        <div>Media: {orderData.mediaFee}%</div>
                      )}
                      {orderData.feeAdmin && parseFloat(orderData.feeAdmin) > 0 && (
                        <div>Admin: {orderData.feeAdmin}%</div>
                      )}
                    </div>
                  </div>
                </Col>
                <Col lg={4} className="mb-3">
                  <div className="bg-light p-3 rounded text-center">
                    <small className="text-muted fw-semibold d-block">Inversión Neta</small>
                    <div className="h4 fw-bold text-primary mb-0">
                      ${Math.max(0, parseFloat(orderData.inversionBruta || 0) - calculateTotalFees()).toLocaleString('es-ES', { minimumFractionDigits: 2 })}
                    </div>
                    <div className={`small fw-bold mt-1 ${getFeeImpactColor()}`}>
                      Impacto: {orderData.inversionBruta ? 
                        `${((calculateTotalFees() / parseFloat(orderData.inversionBruta)) * 100).toFixed(1)}%`
                        : '0%'
                      }
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Detalles del Período */}
              <Row className="mb-3">
                <Col md={6}>
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon icon="calendar" className="text-muted me-2" />
                    <div>
                      <small className="text-muted fw-semibold d-block">Período de Campaña</small>
                      <div className="fw-semibold">
                        {orderData.fechaDesde ? 
                          `${orderData.fechaDesde.toLocaleDateString('es-ES')} - ${orderData.fechaHasta ? orderData.fechaHasta.toLocaleDateString('es-ES') : 'No definida'}` 
                          : 'Fechas no definidas'
                        }
                      </div>
                    </div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon icon="clock" className="text-muted me-2" />
                    <div>
                      <small className="text-muted fw-semibold d-block">Duración</small>
                      <div className="fw-semibold">
                        {orderData.fechaDesde && orderData.fechaHasta ? 
                          `${Math.ceil((orderData.fechaHasta - orderData.fechaDesde) / (1000 * 60 * 60 * 24))} días` 
                          : 'No calculada'
                        }
                      </div>
                    </div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon icon="dollar-sign" className="text-muted me-2" />
                    <div>
                      <small className="text-muted fw-semibold d-block">Moneda</small>
                      <div className="fw-semibold">{orderData.moneda || 'USD'}</div>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Descripción */}
              {orderData.descripcion && (
                <Row>
                  <Col xs={12}>
                    <div className="border-top pt-3">
                      <small className="text-muted fw-semibold d-block mb-2">Descripción</small>
                      <div className="text-muted fst-italic">{orderData.descripcion}</div>
                    </div>
                  </Col>
                </Row>
              )}

              {/* Notas Adicionales */}
              {orderData.notes && (
                <Row className="mt-3">
                  <Col xs={12}>
                    <div className="border-top pt-3">
                      <small className="text-muted fw-semibold d-block mb-2">Notas Adicionales</small>
                      <div className="text-muted fst-italic">{orderData.notes}</div>
                    </div>
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Indicador de progreso */}
        <Col xs={12}>
          <div className="mt-4 pt-3 border-top">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <small className="text-muted">Paso 4 de 4</small>
              </div>
              <div className="text-end">
                <small className="text-success fw-bold">
                  <FontAwesomeIcon icon="check-circle" className="me-1" />
                  ¡Listo para crear la orden!
                </small>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

FinalSummaryStep.propTypes = {
  availableAccounts: PropTypes.array.isRequired
};

export default FinalSummaryStep; 